/**
 * Layout Component (Solid.js)
 * Sidebar + Content + Footer
 */
import type { JSX } from 'solid-js';
import { Show, createSignal } from 'solid-js';
import { Navigate } from '@solidjs/router';
import type { RouteSectionProps } from '@solidjs/router';
import { css } from '../../styled-system/css';
import { ProtectedRoute } from './ProtectedRoute';
import Navigation from './Navigation';
import Footer from './Footer';
import FooterLinks from './FooterLinks';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import AchievementNotification from './AchievementNotification';
import EventBanner from './EventBanner';

interface LayoutProps {
  children: JSX.Element;
  /** Wenn true: Navbar/Footer anzeigen ohne Login (z. B. Startseite). */
  public?: boolean;
  requireAdmin?: boolean;
  requireSupportAdmin?: boolean;
}

const container = css({
  minHeight: 'var(--vh-full)',
  height: { lg: 'var(--vh-full)' },
  display: 'grid',
  gridTemplateColumns: { base: 'minmax(0, 1fr)', lg: 'minmax(token(sizes.layout.sidebar), token(sizes.layout.sidebar)) minmax(0, 1fr)' },
  gridTemplateAreas: { base: '"main"', lg: '"aside main"' },
  gridTemplateRows: { base: 'auto', lg: 'var(--vh-full)' },
  alignItems: { base: 'stretch', lg: 'start' },
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: 0,
  overflowX: 'hidden',
  // Desktop: Container selbst scrollt NICHT – nur der Content-Bereich scrollt
  overflow: { lg: 'hidden' },
  position: 'relative',
  boxSizing: 'border-box',
});

const containerNoSidebar = css({
  minHeight: 'var(--vh-full)',
  height: { lg: 'var(--vh-full)' },
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: 0,
  overflowX: 'hidden',
  overflowY: { lg: 'auto' },
  position: 'relative',
  boxSizing: 'border-box',
});

// Für no-sidebar (öffentliche Seiten): main wächst mit Inhalt, Footer immer ganz unten
const mainPublic = css({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  width: '100%',
});

const main = css({
  gridArea: 'main',
  display: 'flex',
  flexDirection: 'column',
  minHeight: { base: 'auto', lg: 0 },
  height: { lg: 'var(--vh-full)' },
  overflow: { lg: 'hidden' },
  width: '100%',
  minWidth: 0,
});

const content = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  // Mobile: topbar-height + safe-area-inset-top (Notch/Dynamic Island auf iOS im PWA-Modus)
  pt: { base: '[calc(token(sizes.layout.topbar-height) + env(safe-area-inset-top) + 8px)]', lg: '0' },
  // Mobile: Platz für den fixierten Uhr-Footer. Desktop: kein Padding nötig (Footer ist im Fluss)
  pb: { base: '90px', lg: '0' },
  minHeight: 0,
  overflowY: { lg: 'auto' },
  overflowX: 'hidden',
  width: '100%',
  minWidth: 0,
});

const contentNoSidebar = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  // Mobile: Platz für den fixierten Uhr-Footer
  pb: { base: '90px', lg: '0' },
  width: '100%',
  minWidth: 0,
});

function LayoutContent(props: { children: JSX.Element; isPublic?: boolean }) {
  const auth = useAuth();
  const showSidebar = () => !props.isPublic || auth.isAuthenticated;
  const [footerOpen, setFooterOpen] = createSignal(false);
  const toggleFooter = () => setFooterOpen((o) => !o);

  return (
    <Show
      when={showSidebar()}
      fallback={
        <div class={containerNoSidebar}>
          <main class={mainPublic}>
            <div class={contentNoSidebar}>
              {props.children}
            </div>
            <FooterLinks isOpen={footerOpen()} />
            <Footer isOpen={footerOpen()} onToggle={toggleFooter} />
          </main>
        </div>
      }
    >
      <div class={container}>
        <Navigation />
        <main class={main}>
          <div class={content}>
            {props.children}
          </div>
          <FooterLinks isOpen={footerOpen()} />
          <Footer isOpen={footerOpen()} onToggle={toggleFooter} />
        </main>
      </div>
    </Show>
  );
}

export default function Layout(props: LayoutProps) {
  const content = <LayoutContent isPublic={props.public}>{props.children}</LayoutContent>;
  if (props.public) {
    return content;
  }
  return (
    <ProtectedRoute requireAdmin={props.requireAdmin} requireSupportAdmin={props.requireSupportAdmin}>
      {content}
    </ProtectedRoute>
  );
}

// ─── Shell-Komponenten für nested routing ────────────────────────────────────
// Navigation wird NUR EINMAL gemountet; <Outlet /> tauscht nur den Seiteninhalt.

function ShellLayout(props: RouteSectionProps & { requireAdmin?: boolean; requireSupportAdmin?: boolean }) {
  const auth = useAuth();
  const [footerOpen, setFooterOpen] = createSignal(false);
  const toggleFooter = () => setFooterOpen((o) => !o);

  return (
    <Show when={!auth.isLoading} fallback={<LoadingSpinner fullScreen />}>
      <Show when={auth.isAuthenticated} fallback={<Navigate href="/login" />}>
        <Show
          when={!props.requireAdmin || auth.user?.role === 'admin'}
          fallback={<Navigate href="/dashboard" />}
        >
          <Show
            when={!props.requireSupportAdmin || auth.user?.role === 'admin' || auth.user?.role === 'supporter'}
            fallback={<Navigate href="/dashboard" />}
          >
            <div class={container}>
              <Navigation />
              <main class={main}>
                <div class={content}>
                  <EventBanner />
                  {props.children}
                </div>
                <FooterLinks isOpen={footerOpen()} />
                <Footer isOpen={footerOpen()} onToggle={toggleFooter} />
              </main>
              <AchievementNotification />
            </div>
          </Show>
        </Show>
      </Show>
    </Show>
  );
}

/** Shell für alle normalen eingeloggten Seiten – Navigation bleibt persistent */
export function AuthShell(props: RouteSectionProps) {
  return <ShellLayout {...props} />;
}

/** Shell für Admin-Only-Seiten (ohne normale Navigation) */
export function AdminShell(props: RouteSectionProps) {
  const auth = useAuth();
  return (
    <Show when={!auth.isLoading} fallback={<LoadingSpinner fullScreen />}>
      <Show when={auth.isAuthenticated} fallback={<Navigate href="/login" />}>
        <Show when={auth.user?.role === 'admin'} fallback={<Navigate href="/dashboard" />}>
          {props.children}
        </Show>
      </Show>
    </Show>
  );
}

/** Shell für Support-Admin-Seiten */
export function SupportAdminShell(props: RouteSectionProps) {
  return <ShellLayout {...props} requireSupportAdmin />;
}
